import { Text } from '@/app/components/Text/Text'
import classNames from 'classnames'
import { useLayoutEffect, useRef, useState } from 'preact/hooks'
import styles from './index.module.scss'

export type SwitchOption<T extends string> = {
  value: T
  text: string
}

export function Switch<T extends string>(props: {
  value: T
  options: SwitchOption<T>[]
  disabled?: boolean
  color?: 'violet' | 'black'
  onChange: (value: T) => void
}) {
  const [indicator, setIndicator] = useState({ left: 0, width: 0 })
  const buttonRefs = useRef<Partial<Record<T, HTMLButtonElement | null>>>({})

  useLayoutEffect(() => {
    const button = buttonRefs.current[props.value]

    if (!button) return

    const updateIndicator = () => {
      setIndicator({
        left: button.offsetLeft,
        width: button.offsetWidth,
      })
    }

    updateIndicator()

    const resizeObserver = new ResizeObserver(updateIndicator)
    resizeObserver.observe(button)
    window.addEventListener('resize', updateIndicator)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', updateIndicator)
    }
  }, [props.value, props.options])

  return (
    <div class={classNames(styles.root, styles[props.color ?? 'violet'])}>
      <div
        class={styles.indicator}
        style={{
          opacity: indicator.width ? 1 : 0,
          transform: `translateX(${indicator.left}px)`,
          width: indicator.width,
        }}
      />

      {props.options.map(option => (
        <button
          key={option.value}
          ref={el => {
            buttonRefs.current[option.value] = el
          }}
          type='button'
          aria-pressed={props.value === option.value}
          disabled={props.disabled}
          class={classNames(styles.item, {
            [styles.active]: props.value === option.value,
          })}
          onClick={() => {
            props.onChange(option.value)
          }}
        >
          <Text fontSize={14}>
            {option.text}
          </Text>
        </button>
      ))}
    </div>
  )
}
